<script setup lang="ts">
// One message inside a group: body (with edits/deletes applied), reply quote, media,
// reaction chips, and hover actions (a long press on touch screens).
import { computed, nextTick, onUnmounted, ref } from 'vue'
import type { StoredMessage } from '../storage/db'
import { QUICK_REACTIONS, preview, type MessageState } from '../lib/annotations'
import { displayName } from '../lib/format'
import { mentionable, splitMentions, type Segment } from '../lib/mentions'
import { splitLinks } from '../lib/links'
import { useSession } from '../stores/session'
import { mayModerate } from '../lib/roles'
import { describeError } from '../api/http'
import { useComposer } from '../stores/composer'
import { useSectionNav } from '../lib/sectionNav'
import MediaView from './MediaView.vue'
import LibraryCard from './LibraryCard.vue'
import ReportModal from './ReportModal.vue'
import EmojiPickerModal from './EmojiPickerModal.vue'

const props = defineProps<{
  msg: StoredMessage
  state: MessageState | undefined
  replyTo: StoredMessage | undefined
  replyState: MessageState | undefined
  convId: string
  /** Shown in this thread's panel (its top-level message): replying to that needs no quote. */
  thread?: string
}>()
const session = useSession()
const composer = useComposer()
/** Replying opens the message's thread (?thread=<id>), Slack-style. */
const threadNav = useSectionNav('thread')
const reply = () => threadNav.open(props.msg.envelope.id)
const edit = () => composer.edit(props.convId, props.msg, body.value ?? '', props.thread)
const picking = ref(false)
/** The full emoji picker, past the quick reactions. */
const choosing = ref(false)
const e = computed(() => props.msg.envelope)
const mine = computed(() => props.msg.sender.user_id === session.me)
const deleted = computed(() => props.state?.deleted ?? false)
const body = computed(() => (props.state ? props.state.body : 'body' in e.value ? (e.value.body ?? null) : null))
// Names of people here after an `@` show as mentions; one of me marks the whole message.
const segments = computed(() =>
  body.value?.includes('@') ? splitMentions(body.value, mentionable(session.profiles, session.spaces, session.conversations.get(props.convId))) : [{ text: body.value ?? '' }],
)
// Plain runs split further into links.
const parts = computed<(Segment & { href?: string })[]>(() => segments.value.flatMap((s) => (s.user ? [s] : splitLinks(s.text))))
const mentionsMe = computed(() => !mine.value && segments.value.some((s) => s.user === session.me))
const reactions = computed(() => [...(props.state?.reactions.entries() ?? [])])
const replyId = computed(() => (e.value.type === 'text' ? e.value.reply_to : undefined))

async function react(emoji: string): Promise<void> {
  picking.value = false
  const mineAlready = props.state?.reactions.get(emoji)?.includes(session.me ?? '') ?? false
  await session.react(props.convId, props.msg.envelope.id, emoji, mineAlready)
}

async function remove(): Promise<void> {
  if (confirm('Delete this message for everyone?')) await session.deleteMessage(props.convId, props.msg.envelope.id)
}

// More: actions anyone has on any message.
const more = ref(false)

// Touch screens have no hover: a long press lifts the message out over the chat, with
// quick reactions above it and every action listed below (like Signal).
const LONG_PRESS_MS = 450
const row = ref<HTMLElement | null>(null)
const sheet = ref<HTMLElement | null>(null)
const held = ref(false)
const sheetTop = ref(0)
let armed = false
let press: { timer: ReturnType<typeof setTimeout>; x: number; y: number } | null = null
function pressStart(ev: PointerEvent): void {
  if (ev.pointerType !== 'touch' || deleted.value) return
  pressEnd()
  press = { timer: setTimeout(hold, LONG_PRESS_MS), x: ev.clientX, y: ev.clientY }
}
function pressMove(ev: PointerEvent): void {
  // Scrolling, not pressing.
  if (press && Math.hypot(ev.clientX - press.x, ev.clientY - press.y) > 10) pressEnd()
}
function pressEnd(): void {
  if (press) clearTimeout(press.timer)
  press = null
}
async function hold(): Promise<void> {
  press = null
  more.value = picking.value = false
  const at = row.value?.getBoundingClientRect().top ?? 0
  held.value = true
  armed = false
  // The finger is still down: the lift that ends this press mustn't tap what's under it.
  window.addEventListener('pointerup', () => setTimeout(() => (armed = true)), { once: true })
  navigator.vibrate?.(10)
  // Keep the message where it was, with the reactions just above, as far as the screen allows.
  await nextTick()
  const h = sheet.value?.offsetHeight ?? 0
  const reactionsH = (sheet.value?.firstElementChild as HTMLElement | null)?.offsetHeight ?? 0
  sheetTop.value = Math.max(8, Math.min(at - reactionsH - 8, window.innerHeight - h - 8))
}
function unarmedTap(ev: Event): void {
  if (armed) return
  ev.preventDefault()
  ev.stopPropagation()
}
function onContextMenu(ev: Event): void {
  if (press || held.value) ev.preventDefault()
}
/** Runs a sheet action, closing the sheet first. */
function act(fn: () => unknown): void {
  held.value = false
  void fn()
}
onUnmounted(pressEnd)
const mineReacted = (emoji: string) => props.state?.reactions.get(emoji)?.includes(session.me ?? '') ?? false
async function copyText(): Promise<void> {
  more.value = false
  await navigator.clipboard?.writeText(body.value ?? '').catch(() => undefined)
}

// Moderation (HRTH-95): hidden messages show as a placeholder; whoever may hide this
// author's messages can reveal them for review, and hide or un-hide them for everyone.
const space = computed(() => {
  const c = session.conversations.get(props.convId)
  return c?.space_id ? (session.spaces.find((s) => s.id === c.space_id) ?? null) : null
})
const canHide = computed(
  () => !!space.value && !!session.me && mayModerate(space.value, session.me, props.msg.sender.user_id, 'hide_messages'),
)
const hidden = computed(() => props.state?.hidden ?? false)
const revealed = ref(false)
const reporting = ref(false)
const shown = computed(() => !hidden.value || (canHide.value && revealed.value))
async function setHidden(h: boolean): Promise<void> {
  more.value = false
  if (h && !confirm('Hide this message for everyone? Moderators can still see and un-hide it.')) return
  try {
    await session.hideMessage(props.convId, props.msg.envelope.id, h)
  } catch (err) {
    session.toast(describeError(err), 'error')
  }
}
async function removeFromDevice(): Promise<void> {
  more.value = false
  const what = e.value.type === 'media' ? 'this message and its file' : 'this message'
  if (confirm(`Delete ${what} from this device? Others still have it.`)) await session.deleteFromDevice(props.convId, props.msg.envelope.id)
}
</script>

<template>
  <div
    ref="row"
    class="group/msg relative -mx-2 px-2 py-px hover:bg-bg-hover/60 [@media(hover:none)]:py-1 [@media(hover:none)]:select-none [@media(hover:none)]:[-webkit-touch-callout:none]"
    data-testid="message"
    :data-msg-id="msg.envelope.id"
    @mouseleave="more = false"
    @pointerdown="pressStart"
    @pointermove="pressMove"
    @pointerup="pressEnd"
    @pointercancel="pressEnd"
    @contextmenu="onContextMenu"
  >
    <!-- The group's sender and time, on its first message -->
    <slot name="head" />
    <div
      v-if="replyId && replyId !== thread"
      class="mb-0.5 flex items-center gap-2 truncate border-l border-border-strong pl-2 text-xs text-text-muted"
      data-testid="reply-quote"
    >
      <span class="text-text-secondary">{{ replyTo ? displayName(session.profiles, replyTo.sender.user_id) : '' }}</span>
      <span class="truncate">{{ preview(replyTo, replyState) }}</span>
    </div>

    <p v-if="deleted" class="mono text-text-disabled" data-testid="message-deleted">[message deleted]</p>
    <p v-else-if="!shown" class="mono flex items-center gap-2 text-text-disabled" data-testid="message-hidden">
      [hidden by a moderator]
      <button v-if="canHide" class="uppercase underline hover:text-text-primary" data-testid="message-reveal" @click="revealed = true">Show</button>
    </p>
    <div v-else :class="hidden ? 'opacity-50' : ''">
      <p v-if="hidden" class="mono flex items-center gap-2 text-signal" data-testid="message-revealed">
        Hidden for everyone else
        <button class="uppercase underline hover:text-text-primary" @click="revealed = false">Hide</button>
      </p>
      <MediaView v-if="e.type === 'media'" :media="e.media" :conv="convId" />
      <LibraryCard v-else-if="e.type === 'library'" :item="e.item" />
      <p
        v-if="body"
        class="text-[14.5px] leading-relaxed break-words whitespace-pre-wrap text-text-secondary"
        :class="mentionsMe ? 'border-l-2 border-signal pl-2' : ''"
        data-testid="message-body"
      ><template v-for="(s, i) in parts" :key="i"><span
        v-if="s.user"
        class="font-medium"
        :class="s.user === session.me ? 'bg-signal/15 text-text-primary' : 'text-text-primary'"
        data-testid="mention"
      >{{ s.text }}</span><a
        v-else-if="s.href"
        :href="s.href"
        target="_blank"
        rel="noopener noreferrer nofollow"
        class="break-all text-text-primary underline underline-offset-2 hover:text-signal"
        data-testid="message-link"
      >{{ s.text }}</a><template v-else>{{ s.text }}</template></template><span v-if="state?.edited" class="mono ml-2 text-text-disabled" data-testid="edited">(edited)</span></p>
    </div>

    <div v-if="reactions.length && !deleted && shown" class="mt-1 flex flex-wrap gap-1">
      <button
        v-for="[emoji, users] in reactions"
        :key="emoji"
        class="mono border px-1.5 py-0.5"
        :class="users.includes(session.me ?? '') ? 'border-ink text-text-primary' : 'border-border text-text-muted hover:border-border-strong'"
        :title="users.map((u) => displayName(session.profiles, u)).join(', ')"
        data-testid="reaction"
        @click="react(emoji)"
      >
        {{ emoji }} {{ users.length }}
      </button>
    </div>

    <div
      v-if="!deleted"
      class="mono absolute -top-3 right-2 hidden border border-border-strong bg-bg-primary group-hover/msg:flex group-focus-within/msg:flex"
      data-testid="message-actions"
    >
      <button class="px-2 py-1 text-text-muted hover:bg-ink hover:text-paper" title="React" aria-label="React" data-testid="action-react" @click="picking = !picking">
        <!-- A smiley with a plus: add a reaction -->
        <svg viewBox="0 0 16 16" class="h-3.5 w-3.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true">
          <path d="M13.9 7.2A6 6 0 1 1 8.8 2.1" />
          <path d="M5.5 9.5c.6.9 1.5 1.4 2.5 1.4s1.9-.5 2.5-1.4" />
          <path d="M5.8 6.2h.01M10.2 6.2h.01" stroke-width="2" />
          <path d="M13 1v4M11 3h4" />
        </svg>
      </button>
      <button v-if="!thread" class="px-2 py-1 text-text-muted hover:bg-ink hover:text-paper" title="Reply in thread" aria-label="Reply in thread" data-testid="action-reply" @click="reply">↩</button>
      <template v-if="mine && e.type === 'text'">
        <button class="px-2 py-1 text-text-muted hover:bg-ink hover:text-paper" title="Edit" aria-label="Edit" data-testid="action-edit" @click="edit">✎</button>
      </template>
      <button v-if="mine" class="px-2 py-1 text-text-muted hover:bg-signal hover:text-white" title="Delete for everyone" aria-label="Delete for everyone" data-testid="action-delete" @click="remove">✕</button>
      <button class="px-2 py-1 text-text-muted hover:bg-ink hover:text-paper" title="More" aria-label="More" :aria-expanded="more" data-testid="action-more" @click="more = !more">⋯</button>
    </div>
    <div
      v-if="more"
      class="mono absolute top-5 right-2 z-10 flex min-w-44 flex-col border border-border-strong bg-bg-primary py-1"
      data-testid="message-more"
      @keydown.esc="more = false"
    >
      <button v-if="body && shown" class="px-3 py-1.5 text-left text-text-secondary uppercase hover:bg-ink hover:text-paper" data-testid="action-copy" @click="copyText">
        Copy text
      </button>
      <button class="px-3 py-1.5 text-left text-text-secondary uppercase hover:bg-ink hover:text-paper" data-testid="action-delete-local" @click="removeFromDevice">
        Delete from device
      </button>
      <button
        v-if="!mine"
        class="px-3 py-1.5 text-left text-text-secondary uppercase hover:bg-signal hover:text-white"
        data-testid="action-report"
        @click="((more = false), (reporting = true))"
      >
        Report
      </button>
      <button
        v-if="canHide"
        class="px-3 py-1.5 text-left text-text-secondary uppercase hover:bg-ink hover:text-paper"
        :data-testid="hidden ? 'action-unhide' : 'action-hide'"
        @click="setHidden(!hidden)"
      >
        {{ hidden ? 'Un-hide for everyone' : 'Hide for everyone' }}
      </button>
    </div>
    <Teleport v-if="held" to="body">
      <div class="fixed inset-0 z-50 select-none [-webkit-touch-callout:none]" data-testid="message-sheet" @contextmenu.prevent @click.capture="unarmedTap">
        <button class="absolute inset-0 h-full w-full bg-black/70 backdrop-blur-[2px]" aria-label="Close" data-testid="sheet-backdrop" @click="held = false" />
        <div ref="sheet" class="absolute inset-x-4 mx-auto max-h-[calc(100dvh-16px)] max-w-md space-y-2 overflow-y-auto" :style="{ top: `${sheetTop}px` }">
          <div class="flex w-fit border border-border-strong bg-bg-primary" data-testid="sheet-reactions">
            <button
              v-for="r in QUICK_REACTIONS"
              :key="r"
              class="px-2.5 py-2 text-xl"
              :class="mineReacted(r) ? 'bg-bg-hover' : 'grayscale'"
              :aria-label="`React ${r}`"
              @click="act(() => react(r))"
            >
              {{ r }}
            </button>
            <button class="px-3 py-2 text-xl text-text-muted" aria-label="More reactions" data-testid="sheet-more-reactions" @click="act(() => (choosing = true))">…</button>
          </div>
          <div class="border border-border-strong bg-bg-elevated px-3 py-2">
            <div class="mono text-text-muted">{{ displayName(session.profiles, msg.sender.user_id) }}</div>
            <!-- As it looks in the chat; taps here belong to the sheet, not the player -->
            <div v-if="shown && e.type !== 'text'" class="pointer-events-none max-h-[40dvh] overflow-hidden" data-testid="sheet-attachment">
              <MediaView v-if="e.type === 'media'" :media="e.media" />
              <LibraryCard v-else-if="e.type === 'library'" :item="e.item" />
            </div>
            <p v-if="shown ? body : true" class="line-clamp-6 break-words whitespace-pre-wrap text-text-primary">{{ shown ? body : preview(msg, state) }}</p>
          </div>
          <div class="mono flex w-fit min-w-52 flex-col border border-border-strong bg-bg-primary py-1 text-text-secondary">
            <button v-if="!thread" class="px-4 py-3 text-left uppercase hover:bg-ink hover:text-paper" data-testid="sheet-reply" @click="act(reply)">Reply in thread</button>
            <button v-if="mine && e.type === 'text'" class="px-4 py-3 text-left uppercase hover:bg-ink hover:text-paper" data-testid="sheet-edit" @click="act(edit)">Edit</button>
            <button v-if="body && shown" class="px-4 py-3 text-left uppercase hover:bg-ink hover:text-paper" data-testid="sheet-copy" @click="act(copyText)">Copy text</button>
            <button v-if="!mine" class="px-4 py-3 text-left uppercase hover:bg-signal hover:text-white" data-testid="sheet-report" @click="act(() => (reporting = true))">Report</button>
            <button v-if="canHide" class="px-4 py-3 text-left uppercase hover:bg-ink hover:text-paper" data-testid="sheet-hide" @click="act(() => setHidden(!hidden))">
              {{ hidden ? 'Un-hide for everyone' : 'Hide for everyone' }}
            </button>
            <button class="px-4 py-3 text-left uppercase hover:bg-ink hover:text-paper" data-testid="sheet-delete-local" @click="act(removeFromDevice)">Delete from device</button>
            <button v-if="mine" class="px-4 py-3 text-left text-error uppercase hover:bg-signal hover:text-white" data-testid="sheet-delete" @click="act(remove)">
              Delete for everyone
            </button>
          </div>
        </div>
      </div>
    </Teleport>
    <EmojiPickerModal v-if="choosing" @pick="react" @close="choosing = false" />
    <ReportModal v-if="reporting" :conv-id="convId" :msg="msg" @close="reporting = false" />
    <div v-if="picking" class="absolute -top-12 right-2 z-10 flex border border-border-strong bg-bg-primary" data-testid="reaction-picker">
      <button v-for="r in QUICK_REACTIONS" :key="r" class="px-2 py-1 text-lg grayscale hover:bg-bg-hover hover:grayscale-0" @click="react(r)">{{ r }}</button>
      <button
        class="px-2 py-1 text-lg text-text-muted hover:bg-bg-hover hover:text-text-primary"
        aria-label="More reactions"
        data-testid="more-reactions"
        @click="((picking = false), (choosing = true))"
      >
        …
      </button>
    </div>
  </div>
</template>
